import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

export default function FAQ() {
  const { t } = useTranslation()
  const [openQuestion, setOpenQuestion] = useState<number | null>(null)

  const toggleQuestion = (index: number) => {
    setOpenQuestion(openQuestion === index ? null : index)
  }

  const faqs = [
    {
      id: 1,
      question: t('faq.questions.q1.question'),
      answer: t('faq.questions.q1.answer')
    },
    {
      id: 2,
      question: t('faq.questions.q2.question'),
      answer: t('faq.questions.q2.answer')
    },
    {
      id: 3,
      question: t('faq.questions.q3.question'),
      answer: t('faq.questions.q3.answer')
    },
    {
      id: 4,
      question: t('faq.questions.q4.question'),
      answer: t('faq.questions.q4.answer')
    },
    {
      id: 5,
      question: t('faq.questions.q5.question'),
      answer: t('faq.questions.q5.answer')
    },
    {
      id: 7,
      question: t('faq.questions.q7.question'),
      answer: t('faq.questions.q7.answer')
    }
  ]

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section
        className="relative min-h-[400px] md:min-h-[500px] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(226, 114, 91, 0.85), rgba(226, 114, 91, 0.85)), url('https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/Gemini_Generated_Image_4bufq74bufq74buf.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 md:mb-6">
            {t('faq.hero.title')}
          </h1>
          <p className="text-lg md:text-xl lg:text-2xl text-white/95 max-w-2xl mx-auto">
            {t('faq.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="px-4 py-12 md:py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#333333] mb-3 md:mb-4">
              {t('faq.questions.sectionTitle')}
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              {t('faq.questions.sectionSubtitle')}
            </p>
          </div>

          <div className="space-y-3 md:space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={faq.id}
                className="bg-white rounded-xl border-2 border-gray-100 overflow-hidden transition-all duration-300 hover:border-[#E2725B] hover:shadow-lg"
              >
                <button
                  onClick={() => toggleQuestion(index)}
                  className="w-full px-4 md:px-6 py-4 md:py-5 flex items-center justify-between gap-4 text-left group"
                  aria-expanded={openQuestion === index}
                >
                  <div className="flex items-start gap-3 md:gap-4 flex-1">
                    <div className="bg-gradient-to-br from-[#E2725B] to-[#FFDB58] w-8 h-8 md:w-10 md:h-10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-white font-bold text-sm md:text-base">
                        {index + 1}
                      </span>
                    </div>
                    <h3 className="text-base md:text-lg lg:text-xl font-semibold text-[#333333] group-hover:text-[#E2725B] transition-colors">
                      {faq.question}
                    </h3>
                  </div>
                  <div className="flex-shrink-0">
                    <svg
                      className={`w-5 h-5 md:w-6 md:h-6 text-[#E2725B] transition-transform duration-300 ${
                        openQuestion === index ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openQuestion === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-4 md:px-6 pb-4 md:pb-5 pl-12 md:pl-16">
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Carousel Section */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-white to-[#FFDB58]/5">
        <div className="w-full">
          <div className="text-center mb-8 md:mb-12 px-4">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#333333] mb-3 md:mb-4">
              {t('faq.productsCarousel.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('faq.productsCarousel.subtitle')}
            </p>
          </div>

          {/* Horizontal Scrolling Container */}
          <div className="w-full overflow-x-auto custom-scrollbar">
            <div className="flex gap-6 px-4 md:px-8 lg:px-12 pb-8 min-w-max">
              {[
                {
                  id: 1,
                  name: t('products.product1.name'),
                  price: "$15.99",
                  image: "https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/image-14.jpg",
                  stripeLink: "#"
                },
                {
                  id: 2,
                  name: t('products.product2.name'),
                  price: "$18.99",
                  image: "https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/image-15.jpg",
                  stripeLink: "#"
                },
                {
                  id: 3,
                  name: t('products.product3.name'),
                  price: "$16.99",
                  image: "https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/image-16.jpg",
                  stripeLink: "#"
                },
                {
                  id: 4,
                  name: t('products.product4.name'),
                  price: "$17.99",
                  image: "https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/image-2.jpg",
                  stripeLink: "#"
                },
                {
                  id: 5,
                  name: t('products.product5.name'),
                  price: "$14.99",
                  image: "https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/image-12.jpg",
                  stripeLink: "#"
                },
                {
                  id: 6,
                  name: t('products.product6.name'),
                  price: "$19.99",
                  image: "https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/image-10.jpg",
                  stripeLink: "#"
                },
                {
                  id: 7,
                  name: t('products.product7.name'),
                  price: "$16.99",
                  image: "https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/image-7.jpg",
                  stripeLink: "#"
                },
                {
                  id: 8,
                  name: t('products.product8.name'),
                  price: "$17.99",
                  image: "https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/image-6.jpg",
                  stripeLink: "#"
                },
                {
                  id: 9,
                  name: t('products.product9.name'),
                  price: "$18.99",
                  image: "https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/projects/1763352751384_x4pcvj25/image-5.jpg",
                  stripeLink: "#"
                }
              ].map((product, index) => (
                <div
                  key={product.id}
                  className="flex-shrink-0 w-[300px] md:w-[340px]"
                  style={{ marginLeft: index === 0 ? '0' : undefined }}
                >
                  <div className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-[#E2725B]/40 h-full">
                    {/* Product Image */}
                    <div className="relative h-[320px] overflow-hidden bg-gray-50">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-contain p-4 transition-transform duration-300 hover:scale-105"
                      />
                      {/* Price Badge */}
                      <div className="absolute top-3 right-3 bg-[#E2725B] text-white px-4 py-2 rounded-full shadow-lg font-bold text-lg">
                        {product.price}
                      </div>
                    </div>

                    {/* Product Info */}
                    <div className="p-5">
                      <h3 className="text-lg md:text-xl font-bold text-[#333333] mb-4 line-clamp-2 min-h-[3.5rem]">
                        {product.name}
                      </h3>

                      {/* Buy Now Button */}
                      <Link
                        to={`/product/${product.id}`}
                        className="group/btn w-full min-h-[44px] px-5 py-3 bg-[#4D5D33] hover:bg-[#3d4a28] active:bg-[#3d4a28] text-white text-base font-bold rounded-lg transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg"
                      >
                        <svg
                          className="w-5 h-5 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                        <span className="font-bold">{t('faq.productsCarousel.buyNow')}</span>
                        <svg
                          className="w-4 h-4 ml-2 group-hover/btn:translate-x-0.5 transition-transform duration-200"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="text-center mt-4 px-4">
            <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
              </svg>
              Scroll to see all products
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </p>
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA Section */}
      <section className="px-4 py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#E2725B] to-[#FFDB58]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
            {t('faq.cta.title')}
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-white/95 mb-6 md:mb-8 max-w-2xl mx-auto">
            {t('faq.cta.subtitle')}
          </p>
          <div className="flex justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-6 md:px-8 h-12 md:h-14 bg-white text-[#E2725B] font-semibold rounded-full hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl text-base md:text-lg w-full sm:w-auto max-w-xs"
            >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <span>{t('faq.cta.contactButton')}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Support Resources Section */}
      <section className="px-4 py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#333333] mb-3 md:mb-4">
              {t('faq.resources.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('faq.resources.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 rounded-2xl p-6 md:p-8 hover:border-[#E2725B] hover:shadow-xl transition-all duration-300">
              <h3 className="text-xl md:text-2xl font-bold text-[#333333] mb-2 md:mb-3">
                {t('faq.resources.email.title')}
              </h3>
              <p className="text-sm md:text-base text-gray-600 mb-4">
                {t('faq.resources.email.description')}
              </p>
              <a
                href="mailto:support@masalachai.com"
                className="text-[#E2725B] font-semibold hover:text-[#FFDB58] transition-colors text-sm md:text-base"
              >
                {t('faq.resources.email.cta')} →
              </a>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 rounded-2xl p-6 md:p-8 hover:border-[#E2725B] hover:shadow-xl transition-all duration-300">
              <h3 className="text-xl md:text-2xl font-bold text-[#333333] mb-2 md:mb-3">
                {t('faq.resources.hours.title')}
              </h3>
              <p className="text-sm md:text-base text-gray-600 mb-2">
                <strong>{t('faq.resources.hours.weekdays')}</strong>
              </p>
              <p className="text-sm md:text-base text-gray-600">
                {t('faq.resources.hours.weekdaysTime')}
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 rounded-2xl p-6 md:p-8 hover:border-[#E2725B] hover:shadow-xl transition-all duration-300">
              <h3 className="text-xl md:text-2xl font-bold text-[#333333] mb-2 md:mb-3">
                {t('faq.resources.guides.title')}
              </h3>
              <p className="text-sm md:text-base text-gray-600 mb-4">
                {t('faq.resources.guides.description')}
              </p>
              <a
                href="/about"
                className="text-[#E2725B] font-semibold hover:text-[#FFDB58] transition-colors text-sm md:text-base"
              >
                {t('faq.resources.guides.cta')} →
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
